import React, { useState, useMemo } from 'react';
import { Line, Bar } from 'react-chartjs-2'; // Assuming react-chartjs-2 for charts
import { FaClock, FaRupeeSign, FaCheckCircle, FaFilter, FaUsers } from 'react-icons/fa';

// --- Reusable Components ---

// 1. KPI Card Component
const KPICard = ({ title, value, icon: Icon, colorClass, trend }) => (
  <div className={`p-5 rounded-xl shadow-md transition duration-300 hover:shadow-lg ${colorClass}`}>
    <div className="flex items-center justify-between">
      <div className="text-sm font-semibold text-gray-700">{title}</div>
      <Icon className="text-2xl text-gray-500" />
    </div>
    <div className="mt-2 text-3xl font-bold text-gray-900">
      {value}
    </div>
    <div className={`text-xs mt-1 ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
      {trend}% vs Last Quarter
    </div>
  </div>
);

// 2. Filter Form Component
const AnalyticsFilter = ({ onFilterChange }) => {
  const [department, setDepartment] = useState('All');
  const departments = ['All', 'Technology', 'Finance', 'Marketing', 'Operations'];

  const handleChange = (e) => {
    setDepartment(e.target.value);
    onFilterChange(e.target.value);
  };

  return (
    <div className="bg-white p-4 rounded-xl shadow-md flex justify-between items-center mb-6">
      <h3 className="text-lg font-bold text-gray-700">Filter Analytics</h3>
      <div className="flex items-center space-x-4">
        <FaFilter className="text-blue-500" />
        <select
          value={department}
          onChange={handleChange}
          className="p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out bg-gray-50"
        >
          {departments.map(dep => <option key={dep} value={dep}>{dep}</option>)}
        </select>
      </div>
    </div>
  );
};

// --- Dummy Data (Indian Context) ---
const mockRecruitmentData = {
  All: {
    timeToHire: '32 days',
    costPerHire: 35000, // ₹35,000
    acceptanceRate: '92%',
    totalHires: 15,
  },
  Technology: {
    timeToHire: '45 days',
    costPerHire: 60000,
    acceptanceRate: '85%',
    totalHires: 5,
  },
  // ... more department data
};

const formatCurrency = (amount) => `₹${amount.toLocaleString('en-IN')}`;

// --- Main Dashboard Component ---

const RecruitmentAnalytics = () => {
  const [departmentFilter, setDepartmentFilter] = useState('All');

  const currentData = useMemo(() => {
    return mockRecruitmentData[departmentFilter] || mockRecruitmentData.All;
  }, [departmentFilter]);

  // Data for the Candidate Pipeline (Bar Chart)
  const pipelineData = {
    labels: ['Sourced', 'Screening', 'Interview', 'Offer', 'Hired'],
    datasets: [
      {
        label: `Candidates in Pipeline (${departmentFilter})`,
        data: departmentFilter === 'Technology' ? [50, 25, 15, 8, 5] : [120, 70, 40, 20, 15],
        backgroundColor: ['#A0AEC0', '#4A5568', '#4299E1', '#F6AD55', '#48BB78'],
        borderRadius: 5,
      },
    ],
  };

  // Data for the Time-to-Hire Trend (Line Chart)
  const timeToHireTrend = {
    labels: ['Q1 2024', 'Q2 2024', 'Q3 2024', 'Q4 2024'],
    datasets: [
      {
        label: 'Time to Hire (Days)',
        data: [40, 35, 32, 28],
        fill: false,
        borderColor: '#4299E1',
        tension: 0.1,
      },
    ],
  };

  return (
    <div className="min-h-screen p-4 sm:p-6 md:p-8 bg-gray-50" style={{ backgroundColor: '#F3F4F6' }}>
      <h1 className="text-3xl font-extrabold text-gray-800 mb-6 border-b pb-2">Recruitment Analytics Dashboard 📊</h1>
      
      {/* Filter Component */}
      <AnalyticsFilter onFilterChange={setDepartmentFilter} />

      {/* 1. Key Performance Indicators (KPIs) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <KPICard
          title="Avg. Time-to-Hire"
          value={currentData.timeToHire}
          icon={FaClock}
          colorClass="bg-white"
          trend={-10} // 10% faster
        />
        <KPICard
          title="Avg. Cost-per-Hire"
          value={formatCurrency(currentData.costPerHire)}
          icon={FaRupeeSign}
          colorClass="bg-white"
          trend={5} // 5% increase in cost
        />
        <KPICard
          title="Offer Acceptance Rate"
          value={currentData.acceptanceRate}
          icon={FaCheckCircle}
          colorClass="bg-white"
          trend={2} // 2% better
        />
        <KPICard
          title="Total Hires (Quarterly)"
          value={currentData.totalHires}
          icon={FaUsers}
          colorClass="bg-white"
          trend={15} // 15% more hires
        />
      </div>

      {/* 2. Charts Section - Pipeline and Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        
        {/* Pipeline Distribution Chart (Bar) */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Candidate Pipeline Status</h2>
          <div className="h-80">
             <Bar data={pipelineData} options={{ maintainAspectRatio: false, scales: { y: { beginAtZero: true } } }} />
          </div>
        </div>
        
        {/* Time-to-Hire Trend Chart (Line) */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Time-to-Hire Trend</h2>
          <div className="h-80">
            <Line data={timeToHireTrend} options={{ maintainAspectRatio: false }} />
          </div>
        </div>
      </div>

      {/* 3. Source Performance Table */}
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Recruitment Source Performance (Q4 2024)</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Candidates</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hires</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conversion Rate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg. Cost/Hire</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {/* Dummy Indian Data */}
              {[{ source: 'Naukri.com', cand: 60, hires: 5, rate: '8.3%', cost: 20000 },
                { source: 'Employee Referral', cand: 25, hires: 7, rate: '28.0%', cost: 5000 },
                { source: 'LinkedIn', cand: 30, hires: 2, rate: '6.6%', cost: 45000 },
                { source: 'Careers Page', cand: 55, hires: 1, rate: '1.8%', cost: 0 },
              ].map((item, index) => (
                <tr key={index} className="hover:bg-blue-50 transition duration-150">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.source}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.cand}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-700">{item.hires}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.rate}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(item.cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RecruitmentAnalytics;